import { LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { pageTheme,titleTheme,cardTheme } from '../../components/consoleTheme'
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { User, Mail, Building2, Briefcase, Key, Camera, Check, X, Clock, MonitorSmartphone } from 'lucide-react';

function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
}

async function authFetch(url, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

export default function AccountManagement() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [toast, setToast] = useState(null);

  const [profile, setProfile] = useState({
    name: '', email: '', nickname: '', department: '', position: '', statusMessage: ''
  });
  const [pwd, setPwd] = useState({ current: '', new1: '', new2: '' });
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const loadMe = async () => {
    try {
      const data = await authFetch('/auth/me');
      const u = data.user;
      setMe(u);
      setProfile({
        name: u.name || '',
        email: u.email || '',
        nickname: u.nickname || '',
        department: u.department || '',
        position: u.position || '',
        statusMessage: u.statusMessage || ''
      });
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const loadHistory = async () => {
    try {
      const data = await authFetch('/auth/login-history?limit=10');
      setHistory(data.history || []);
    } catch (e) {/* ignore */}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadMe(), loadHistory()]);
      setLoading(false);
    })();
  }, []);

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      await authFetch('/auth/update-profile', {
        method: 'PUT',
        body: JSON.stringify({
          name: profile.name,
          email: profile.email,
          nickname: profile.nickname,
          department: profile.department,
          position: profile.position,
          statusMessage: profile.statusMessage
        })
      });
      showToast('프로필이 업데이트되었습니다.');
      await loadMe();
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingProfile(false);
  };

  const handlePwdSave = async () => {
    if (!pwd.current || !pwd.new1) return showToast('현재/새 비밀번호를 입력해 주세요.', 'error');
    if (pwd.new1 !== pwd.new2) return showToast('새 비밀번호 확인이 일치하지 않습니다.', 'error');
    if (pwd.new1.length < 8) return showToast('새 비밀번호는 8자 이상이어야 합니다.', 'error');
    setSavingPwd(true);
    try {
      await authFetch('/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword: pwd.current, newPassword: pwd.new1 })
      });
      setPwd({ current: '', new1: '', new2: '' });
      showToast('비밀번호가 변경되었습니다.');
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingPwd(false);
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return showToast('3MB 이하 이미지만 업로드 가능합니다.', 'error');
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await authFetch('/auth/avatar', { method: 'POST', body: fd });
      await loadMe();
      showToast('아바타가 변경되었습니다.');
    } catch (err) {
      showToast(err.message, 'error');
    }
    setUploadingAvatar(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAvatarRemove = async () => {
    if (!window.confirm('아바타를 제거하시겠습니까?')) return;
    try {
      await authFetch('/auth/avatar', { method: 'DELETE' });
      await loadMe();
      showToast('아바타가 제거되었습니다.');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (loading) return <Wrap><LoadingState label="불러오는 중…" /></Wrap>;

  return (
    <Wrap>
      <PageTitle>계정관리</PageTitle>

      {toast && <Toast kind={toast.kind}>
        {toast.kind === 'error' ? <X size={16} /> : <Check size={16} />}
        {toast.msg}
      </Toast>}

      <Card>
        <CardHeader><CardTitle><User size={18} /> 프로필</CardTitle></CardHeader>
        <CardBody>
          <ProfileTop>
            <AvatarBlock>
              <AvatarFrame>
                {me?.avatar ? (
                  <AvatarImg src={me.avatar} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <AvatarFallback>{(me?.name || me?.email || '?')[0]?.toUpperCase()}</AvatarFallback>
                )}
                <AvatarOverlay onClick={() => fileRef.current?.click()}><Camera size={16} /></AvatarOverlay>
              </AvatarFrame>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleAvatarSelect} />
              <AvatarActions>
                <SmallBtn onClick={() => fileRef.current?.click()} disabled={uploadingAvatar}>
                  {uploadingAvatar ? '업로드 중...' : '변경'}
                </SmallBtn>
                {me?.avatar && <SmallBtn danger onClick={handleAvatarRemove}>제거</SmallBtn>}
              </AvatarActions>
              <AvatarHint>JPG/PNG/GIF/WebP, 3MB 이하</AvatarHint>
            </AvatarBlock>

            <ProfileFields>
              <Field>
                <FieldLabel><User size={14} /> 이름</FieldLabel>
                <Input value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel><Mail size={14} /> 이메일</FieldLabel>
                <Input value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel>닉네임</FieldLabel>
                <Input value={profile.nickname} onChange={e => setProfile({ ...profile, nickname: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel><Building2 size={14} /> 소속</FieldLabel>
                <Input value={profile.department} onChange={e => setProfile({ ...profile, department: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel><Briefcase size={14} /> 직급</FieldLabel>
                <Input value={profile.position} onChange={e => setProfile({ ...profile, position: e.target.value })} />
              </Field>
              <Field full>
                <FieldLabel>상태 메시지</FieldLabel>
                <Input value={profile.statusMessage} onChange={e => setProfile({ ...profile, statusMessage: e.target.value })} placeholder="짧은 상태 메시지" />
              </Field>
            </ProfileFields>
          </ProfileTop>

          <CardFooter>
            <PrimaryBtn onClick={handleProfileSave} disabled={savingProfile}>
              {savingProfile ? '저장 중...' : '저장'}
            </PrimaryBtn>
          </CardFooter>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle><Key size={18} /> 비밀번호 변경</CardTitle></CardHeader>
        <CardBody>
          <Field><FieldLabel>현재 비밀번호</FieldLabel>
            <Input type="password" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} />
          </Field>
          <Field><FieldLabel>새 비밀번호 (8자 이상)</FieldLabel>
            <Input type="password" value={pwd.new1} onChange={e => setPwd({ ...pwd, new1: e.target.value })} />
          </Field>
          <Field><FieldLabel>새 비밀번호 확인</FieldLabel>
            <Input type="password" value={pwd.new2} onChange={e => setPwd({ ...pwd, new2: e.target.value })} />
          </Field>
          <CardFooter>
            <PrimaryBtn onClick={handlePwdSave} disabled={savingPwd}>
              {savingPwd ? '변경 중...' : '비밀번호 변경'}
            </PrimaryBtn>
          </CardFooter>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle><Clock size={18} /> 최근 로그인 이력</CardTitle></CardHeader>
        <CardBody>
          {history.length === 0 ? (
            <Empty>이력이 없습니다.</Empty>
          ) : (
            <div style={{overflowX:'auto',maxWidth:'100%'}} tabIndex={0} role="region" aria-label="로그인 이력 표"><HistoryTable>
              <thead>
                <tr><th>일시</th><th>IP</th><th>접속 환경</th><th>결과</th></tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td>{new Date(h.login_at).toLocaleString('ko-KR')}</td>
                    <td><Mono>{h.ip_address || '-'}</Mono></td>
                    <td><UATruncate title={h.user_agent}><MonitorSmartphone size={12} /> {shortUA(h.user_agent)}</UATruncate></td>
                    <td>{h.success ? <SuccessBadge>성공</SuccessBadge> : <FailBadge>{h.fail_reason || '실패'}</FailBadge>}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable></div>
          )}
        </CardBody>
      </Card>
    </Wrap>
  );
}

function shortUA(ua) {
  if (!ua) return '-';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  return ua.slice(0, 30);
}

const Wrap = styled.div`
 ${pageTheme}

  padding: 28px 36px 60px;
  max-width: 960px;
  margin: 0 auto;
  text-align: left;
  @media(max-width:768px){padding:1rem 1rem 2rem;}
`;
const PageTitle = styled.h1`
 ${titleTheme}
 &&{margin-bottom:28px;}

  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px;
`;
const Card = styled.div`
 ${cardTheme}

  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 20px;
  overflow: hidden;
`;
const CardHeader = styled.div`
  padding: 16px 22px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafbfc;
`;
const CardTitle = styled.h2`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const CardBody = styled.div`padding: 22px;@media(max-width:768px){padding:1rem;}`;
const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 16px;
`;
const ProfileTop = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  @media (max-width: 720px) { flex-direction: column; }
`;
const AvatarBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 140px;
  flex-shrink: 0;
`;
const AvatarFrame = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #e5e7eb;
  position: relative;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;
const AvatarImg = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`;
const AvatarFallback = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  font-size: 48px; font-weight: 600; color: #6b7280;
  background: linear-gradient(135deg, #f5f4ff, #e0e7ff);
`;
const AvatarOverlay = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0;
  background: rgba(0,0,0,0.5); color: white;
  display: flex; align-items: center; justify-content: center;
  padding: 6px; cursor: pointer;
  opacity: 0; transition: opacity 0.2s;
  ${AvatarFrame}:hover & { opacity: 1; }
`;
const AvatarActions = styled.div`display: flex; gap: 6px;`;
const AvatarHint = styled.div`font-size: 11px; color: #9ca3af; text-align: center;`;
const ProfileFields = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  min-width: 0;
  @media(max-width:768px){grid-template-columns:minmax(0,1fr);width:100%;}
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
  ${({ full }) => full && 'grid-column: 1 / -1;'}
`;
const FieldLabel = styled.label`
  font-size: 12px; font-weight: 600; color: #4b5563;
  display: flex; align-items: center; gap: 5px;
`;
const Input = styled.input`
  padding: 9px 11px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
`;
const PrimaryBtn = styled.button`
  padding: 9px 22px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SmallBtn = styled.button`
  padding: 5px 12px;
  background: ${({ danger }) => danger ? '#fee2e2' : '#f3f4f6'};
  color: ${({ danger }) => danger ? '#b91c1c' : '#374151'};
  border: 1px solid ${({ danger }) => danger ? '#fecaca' : '#d1d5db'};
  border-radius: 5px;
  font-size: 12px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }
  th {
    background: #fafbfc;
    font-weight: 600;
    color: #6b7280;
    font-size: 12px;
  }
  tr:hover td { background: #fafbfc; }
`;
const Mono = styled.span`
  font-family: 'Cascadia Mono', Consolas, monospace;
  font-size: 12px;
`;
const UATruncate = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  color: #6b7280;
`;
const SuccessBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;
const FailBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;
const Empty = styled.div`
  text-align: center;
  padding: 30px;
  color: #9ca3af;
  font-size: 13px;
`;
const Toast = styled.div`
  position: fixed;
  top: 80px;
  right: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: ${({ kind }) => kind === 'error' ? '#fee2e2' : '#d1fae5'};
  color: ${({ kind }) => kind === 'error' ? '#991b1b' : '#065f46'};
  border: 1px solid ${({ kind }) => kind === 'error' ? '#fca5a5' : '#86efac'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
`;
